import React, { useState, useEffect } from 'react';
import { PostRequest, DeleteRequest } from 'components/common/index.js';
import { getDataSourceIcon, DataSourceConnectButtons } from './DataSourceIcons';
import GoogleSheetsPicker from './form_fields/GoogleSheetsPicker';
import OneDrivePicker from './form_fields/OneDrivePicker';
import BulkMapDataSourceModal from './BulkMapDataSourceModal';
import { DataSourceOAuthProvider, useDataSourceOAuth } from './DataSourceOAuthContext';

const DataSourcesSettingsContent = () => {
  const { dataSources, loading, initiateOAuth, refetchDataSources } = useDataSourceOAuth();
  const [showPickerForDataSource, setShowPickerForDataSource] = useState(null);
  const [showBulkMap, setShowBulkMap] = useState(false);

  const hasSpreadsheetDataSource = dataSources.some(
    (dataSource) => dataSource.source_type === 'google_sheets' || dataSource.source_type === 'microsoft_excel'
  );

  const handleConnectGoogleSheets = () => {
    initiateOAuth(
      '/quote-builder/data-sources/google-sheets/authorize',
      'google_sheets_oauth_complete',
      (result) => {
        if (result.success && result.dataSourceId) {
          const newDataSource = result.dataSources.find(ds => ds.id == result.dataSourceId);
          if (newDataSource && newDataSource.source_type === 'google_sheets') {
            // Auto-open picker if no spreadsheets picked yet
            if (!newDataSource.picked_spreadsheets || newDataSource.picked_spreadsheets.length === 0) {
              setShowPickerForDataSource(previousValue => {
                if (previousValue) return previousValue;
                return newDataSource;
              });
            }
          }
        }
      }
    );
  };

  const handleConnectMicrosoftExcel = () => {
    initiateOAuth(
      '/quote-builder/data-sources/microsoft-excel/authorize',
      'microsoft_excel_oauth_complete',
      (result) => {
        if (result.success && result.dataSourceId) {
          const newDataSource = result.dataSources.find(ds => ds.id == result.dataSourceId);
          if (newDataSource && newDataSource.source_type === 'microsoft_excel') {
            // Auto-open picker if no workbooks picked yet
            if (!newDataSource.picked_workbooks || newDataSource.picked_workbooks.length === 0) {
              setShowPickerForDataSource(previousValue => {
                if (previousValue) return previousValue;
                return newDataSource;
              });
            }
          }
        }
      }
    );
  };

  const handleConnectAirtable = () => {
    initiateOAuth(
      '/quote-builder/data-sources/airtable/authorize',
      'airtable_oauth_complete',
      (result) => {
        // Airtable doesn't need a picker after OAuth
      }
    );
  };

  const handleUnlink = (dataSource) => {
    if (!confirm(t('quote_builder.unlink_data_source_confirm', { name: dataSource.name }))) {
      return;
    }

    PostRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/unlink`,
      callback: () => {
        refetchDataSources();
      }
    });
  };

  const handleRemoveSpreadsheet = (dataSource, spreadsheetId, spreadsheetName) => {
    if (!confirm(t('quote_builder.remove_spreadsheet_confirm', { name: spreadsheetName }))) {
      return;
    }

    DeleteRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/spreadsheets/${encodeURIComponent(spreadsheetId)}`,
      callback: () => {
        refetchDataSources();
      }
    });
  };

  const handleRemoveWorkbook = (dataSource, workbookId, workbookName) => {
    if (!confirm(t('quote_builder.remove_workbook_confirm', { name: workbookName }))) {
      return;
    }

    DeleteRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/workbooks/${encodeURIComponent(workbookId)}`,
      callback: () => {
        refetchDataSources();
      }
    });
  };

  const handleAddSpreadsheet = (dataSource) => {
    setShowPickerForDataSource(dataSource);
  };

  const handleSpreadsheetsPicked = () => {
    setShowPickerForDataSource(null);
    refetchDataSources();
  };

  const handlePickerCancel = () => {
    setShowPickerForDataSource(null);
  };

  const getSourceTypeIcon = (sourceType) => {
    return getDataSourceIcon(sourceType, { height: '20px', marginRight: '8px' });
  };

  const getSourceTypeName = (sourceType) => {
    if (sourceType === 'google_sheets') {
      return t('quote_builder.google_sheets');
    } else if (sourceType === 'microsoft_excel') {
      return t('quote_builder.microsoft_excel');
    } else if (sourceType === 'airtable') {
      return t('quote_builder.airtable');
    }
    return sourceType;
  };

  const getSyncStatusBadge = (status) => {
    const statusClasses = {
      'active': 'label-success',
      'pending': 'label-warning',
      'error': 'label-danger',
      'disconnected': 'label-default'
    };
    const statusLabels = {
      'active': t('quote_builder.sync_status_active'),
      'pending': t('quote_builder.sync_status_pending'),
      'error': t('quote_builder.sync_status_error'),
      'disconnected': t('quote_builder.sync_status_disconnected')
    };
    return <span className={`label ${statusClasses[status] || 'label-default'}`}>{statusLabels[status] || status}</span>;
  };

  const renderPickedFilesList = (dataSource) => {
    // Only show for Google Sheets and Microsoft Excel (not Airtable)
    if (dataSource.source_type !== 'google_sheets' && dataSource.source_type !== 'microsoft_excel') {
      return null;
    }

    const files = dataSource.source_type === 'google_sheets'
      ? (dataSource.picked_spreadsheets || [])
      : (dataSource.picked_workbooks || []);

    const addButtonLabel = dataSource.source_type === 'google_sheets'
      ? t('quote_builder.add_spreadsheet')
      : t('quote_builder.add_workbook');

    return (
      <div style={{ marginTop: '10px', marginLeft: '28px' }}>
        {files.length > 0 && (
          <ul style={{ marginTop: '0', marginBottom: '8px', paddingLeft: '0', listStyle: 'none' }}>
            {files.map((file) => (
              <li key={file.id} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
                <i className="fa fa-file-o" aria-hidden="true" style={{ marginRight: '8px', color: '#666' }}></i>
                <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: '#337ab7' }}>
                  {file.name}
                </a>
                <span style={{ margin: '0 8px', color: '#999' }}>-</span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (dataSource.source_type === 'google_sheets') {
                      handleRemoveSpreadsheet(dataSource, file.id, file.name);
                    } else {
                      handleRemoveWorkbook(dataSource, file.id, file.name);
                    }
                  }}
                  style={{ color: '#999', fontSize: '12px' }}
                >
                  <i className="fa fa-chain-broken" aria-hidden="true" style={{ marginRight: '4px' }}></i>
                  {t('quote_builder.remove_access')}
                </a>
              </li>
            ))}
          </ul>
        )}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); handleAddSpreadsheet(dataSource); }}
          style={{ fontSize: '13px', color: '#5cb85c' }}
        >
          <i className="fa fa-plus" aria-hidden="true" style={{ marginRight: '5px' }}></i>
          {addButtonLabel}
        </a>
      </div>
    );
  };

  const dataSourceRow = (dataSource) => {
    return (
      <tr key={dataSource.id}>
        <td>
          <div>
            {getSourceTypeIcon(dataSource.source_type)}
            {dataSource.name}
          </div>
          {renderPickedFilesList(dataSource)}
        </td>
        <td>{getSourceTypeName(dataSource.source_type)}</td>
        <td>{getSyncStatusBadge(dataSource.sync_status)}</td>
        <td>
          <a href="#" onClick={(e) => { e.preventDefault(); handleUnlink(dataSource); }}>
            <i className="fa fa-chain-broken" aria-hidden="true"></i> {t('quote_builder.unlink')}
          </a>
        </td>
      </tr>
    );
  };

  const listOfDataSources = () => {
    return (
      <table className="table table-striped table-bordered-outside quote_builder_table">
        <tbody>
          <tr>
            <th colSpan="4">{t('quote_builder.my_data_sources')} ({dataSources.length})</th>
          </tr>
          <tr>
            <th>{t('quote_builder.name')}</th>
            <th>{t('quote_builder.source_type')}</th>
            <th>{t('quote_builder.status')}</th>
            <th></th>
          </tr>
          {dataSources.map(dataSourceRow)}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div style={{ marginLeft: '-20px' }}>
        <p><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</p>
      </div>
    );
  }

  return (
    <div style={{ marginLeft: '0px' }}>
      {showPickerForDataSource && showPickerForDataSource.source_type === 'google_sheets' && (
        <GoogleSheetsPicker
          dataSourceId={showPickerForDataSource.id}
          onSpreadsheetsPicked={handleSpreadsheetsPicked}
          onCancel={handlePickerCancel}
        />
      )}
      {showPickerForDataSource && showPickerForDataSource.source_type === 'microsoft_excel' && (
        <OneDrivePicker
          dataSourceId={showPickerForDataSource.id}
          onWorkbooksPicked={handleSpreadsheetsPicked}
          onCancel={handlePickerCancel}
        />
      )}
      {dataSources.length > 0 && listOfDataSources()}

      {hasSpreadsheetDataSource && (
        <div style={{ marginTop: '10px' }}>
          <button type="button" className="btn btn-default" onClick={() => setShowBulkMap(true)}>
            <i className="fa fa-link" aria-hidden="true"></i> {t('quote_builder.bulk_map_open')}
          </button>
        </div>
      )}

      {showBulkMap && (
        <BulkMapDataSourceModal
          dataSources={dataSources}
          onClose={() => { setShowBulkMap(false); refetchDataSources(); }}
        />
      )}

       <div style={{ marginLeft: '-10px' }}>
      {dataSources.length === 0 && (
        <div style={{ marginTop: '10px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <h4>{t('quote_builder.getting_started')}</h4>
          <p dangerouslySetInnerHTML={{ __html: t('quote_builder.data_sources_getting_started_text') }} />
        </div>
      )}
      <br />

        <h4> &nbsp; &nbsp; {t('quote_builder.add_new_data_source')}</h4>
        <div style={{ marginTop: '15px', marginLeft: '10px'}}>
          <DataSourceConnectButtons
            onConnectGoogleSheets={handleConnectGoogleSheets}
            onConnectMicrosoftExcel={handleConnectMicrosoftExcel}
            onConnectAirtable={handleConnectAirtable}
          />
        </div>
        <p style={{ marginTop: '15px' , marginLeft: '10px'}}>
          <a href="https://www.diecuttemplates.com/contact-us?chat=true" target="_blank" rel="noopener noreferrer">
            {t('quote_builder.missing_integration')}
          </a>
        </p>
      </div>

    </div>
  );
};

// Wrap with provider - this component is used standalone on the settings page
const DataSourcesSettings = () => {
  return (
    <DataSourceOAuthProvider>
      <DataSourcesSettingsContent />
    </DataSourceOAuthProvider>
  );
};

export default DataSourcesSettings;
